import React from 'react';
import AlertBadge from '../../Components/Icons/AlertBadge';
import TimeAgo from '../../Components/Formatting/TimeAgo';
import Money from '../../Components/Formatting/Money';
import Dropdown from '../../Components/Dropdown';
import Toggle from '../../Components/Button/Toggle';
import Loading from '../../Components/Loading';
import { useDownloadItemisation } from '../../Services/helpers';
import { useDialogues } from '../../Components/Dialogues';
import { usePreference } from '../../Data/Preference';
import Icon from '../../Components/Icons/Icon';

const invoiceThemes = {
    'Paid': 'success',
    'Outstanding': 'warning',
    'Overdue': 'danger',
    'Deleted': 'secondary',
    // Loyalty card
    'Active': 'info',
    'Available': 'info',
    'Redeemed': 'success',
    'Expired': 'danger',
    'Cancelled': 'danger',
}

const invoiceIcons = {
    'Deleted': 'Trash',
    // Loyalty card
    'Active': 'GiftCard',
    'Available': 'GiftCard',
    'Redeemed': 'GiftCard',
    'Expired': 'GiftCard',
    'Cancelled': 'GiftCard',
}

const Invoices = ({customer, deleteInvoice, cancelLoyaltyCard}) => {
    const [showAdjustments, setShowAdjustments] = usePreference('customer-adjustments', customer?.invoices?.length === 0);
    if (customer.invoices === undefined) {
        return <Loading size="2x" center />;
    }

    const items = [...customer.invoices, ...customer.balanceAdjustments, ...customer.loyaltyCards]
        .sort((a, b) => new Date(b.updatedAt ?? b.adjustedAt ?? b.createdAt) - new Date(a.updatedAt ?? a.adjustedAt ?? b.createdAt) )
                        // Invoice, or loyalty card, or showing adjustments
        .filter(item => item.itemisationId || item.reward || showAdjustments)
        .filter(item => item.status !== 'Deleted');

    return <>
        {(customer.balanceAdjustments.length >= 1) && <div className="pb-3">
            <Toggle onChange={setShowAdjustments}
                enabled={showAdjustments}
                label="Show payments &amp; adjustments"
                className="border border-gray-200 rounded-xl p-2"
            />
        </div>}

        {(customer.invoices.length === 0 && (customer.balanceAdjustments.length === 0 || !showAdjustments)) && <>
            <p className="py-3 text-muted text-center">No invoices yet.</p>
            <p className="py-3 text-muted text-center">
                Invoices are automatically created<br />
                when jobs are marked as done.
            </p>
        </>}

        <div className="flow-root">
            <ul className="-mb-8">
                {items.map((item, itemIdx) => {
                    return <Row key={item.id} {...{item, customer, deleteInvoice, cancelLoyaltyCard}} showLine={itemIdx < items.length - 1} />
                })}
            </ul>
        </div>
    </>
}

const Row = ({item, customer, deleteInvoice, cancelLoyaltyCard, showLine}) => {
    let content;
    if (item.itemisationId) {
        content = <Invoice {...{invoice: item, customer, deleteInvoice}} />
    } else if (item.reward) {
        content = <LoyaltyCard {...{loyaltyCard: item, customer, cancelLoyaltyCard}} />
    } else {
        content = <Adjustment adjustment={item} />
    }

    return <li key={item.id}>
        <div className="relative pb-8">
            {showLine && <span className="absolute left-6 top-5 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />}
            <div className="relative flex space-x-3">
                <div>
                    <AlertBadge theme={invoiceThemes[item.status] ?? 'info'} icon={invoiceIcons[item.status] ?? null} />
                </div>
                <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5 text-sm">
                    {content}
                </div>
            </div>
        </div>
    </li>
}

const LoyaltyCard = ({loyaltyCard, customer, cancelLoyaltyCard}) => {
    const {confirm} = useDialogues();

    const points = [];
    const redeemedClass = ['Redeemed', 'Cancelled', 'Expired'].includes(loyaltyCard.status) ? 'fill-muted' : 'fill-brand';
    for (let i = 0; i < loyaltyCard.accumulatedPoints; i++) {
        points.push(<Icon key={i} icon="CircleCheck" theme="solid" inline className={`mr-1 ${redeemedClass}`} />)
    }
    for (let i = loyaltyCard.accumulatedPoints; i < loyaltyCard.requiredPoints; i++) {
        points.push(<Icon key={i} icon="Circle" inline className="mr-1 fill-muted" />)
    }

    const redeemingJob = customer.jobs.find(j => j.id === loyaltyCard.redeemingJobId) ?? null;

    let reward = null;
    if (loyaltyCard.reward.type === 'Free') {
        reward = `Receive job #${redeemingJob?.shortId} for free`;
    } else if (loyaltyCard.reward.type === 'Percent') {
        reward = `${loyaltyCard.reward.value}% off job #${redeemingJob?.shortId}`;
    } else if (loyaltyCard.reward.type === 'Cash') {
        reward = <><Money pence={loyaltyCard.reward.value} /> off job #${redeemingJob?.shortId}</>;
    }

    let statusText = null;
    if (['Cancelled', 'Expired'].includes(loyaltyCard.status)) {
        statusText = `(${loyaltyCard.status})`;
    } else if (loyaltyCard.status === 'Redeemed') {
        statusText = <>(Redeemed for <Money pence={loyaltyCard.redeemedValue} />)</>;
    }

    return <ItemLayout
        title={<strong>Loyalty Card {statusText}</strong>}
        description={<>
            <span className="inline-block py-1">{points}</span><br />
            {reward}
        </>}
        date={loyaltyCard.updatedAt}
        disabled={!!customer?.deletedAt}
        options={[
            {
                icon: 'Trash',
                title: 'Cancel loyalty card',
                disabled: ['Redeemed', 'Cancelled', 'Expired'].includes(loyaltyCard.status),
                onClick: () => confirm(
                    {
                        theme: 'danger',
                        title: 'Cancel loyalty card?',
                        message: 'The loyalty card will be removed and the customers will lose any points.'
                    },
                    () => cancelLoyaltyCard(loyaltyCard.id)
                )
            }
        ]}
    />
}

const Invoice = ({invoice, customer, deleteInvoice}) => {
    const {confirm} = useDialogues();
    const downloadItemisation = useDownloadItemisation();

    return <ItemLayout
            title={<strong><Money pence={invoice.total} /> Invoice #{invoice.shortId}</strong>}
            description={<>
                {invoice.status !== 'Paid' && <Money pence={invoice.outstanding} className="mr-1" />}
                {invoice.status}
            </>}
            date={invoice.createdAt}
            disabled={!!customer?.deletedAt}
            options={[
                {
                    icon: 'CloudArrowDown',
                    title: 'Download invoice',
                    onClick: () => downloadItemisation(customer.businessId, invoice.itemisationId)
                },
                {
                    div: true,
                },
                {
                    icon: 'FileInvoice',
                    title: 'Edit invoice',
                    to: `/manage/customers/${invoice.customerId}/itemisations/${invoice.itemisationId}`,
                },
                {
                    icon: 'Trash',
                    title: 'Delete invoice',
                    onClick: () => confirm(
                        {
                            theme: 'danger',
                            title: 'Delete invoice?',
                            message: 'The invoice will be removed and the customers balance will be adjusted accordingly.'
                        },
                        () => deleteInvoice(invoice.id)
                    ),
                }
            ]}
        />
}

const Adjustment = ({adjustment}) => {
    return <ItemLayout
            title={<><Money pence={adjustment.amount} /> {adjustment.type}</>}
            description={adjustment.description}
            date={adjustment.adjustedAt}
            options={null}
        />
}

const ItemLayout = ({title, description, date, options, disabled}) => {
    return <>
        <div>
            <p className="text-muted">
                {title}<br />
                {description}
            </p>
            <p className="text-muted pt-1">
                <TimeAgo dateTime={date} />
            </p>
        </div>
        <div className="text-right">
            {options && <Dropdown {...{options, disabled}} />}
        </div>
    </>
}

export default Invoices;
